import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Image } from "react-native";
import { useFragment } from "react-relay";

import { VStack, Box } from "@whoosh/design-system/src/components/primitive";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { EventDateTimeLocationBar } from "../EventDateTimeLocationBar";
import { EventDetailCapacity } from "../EventDetailCapacity";
import { EventDetailDescription } from "../EventDetailDescription";
import { EventDetailHostsRedesign } from "../EventDetailHostsRedesign";
import { EventDetailMultiSessionDateDisplay } from "../EventDetailMultiSessionDateDisplay";
import { EventDetailPairings } from "../EventDetailPairings";
import { EventDetailSeriesNavButtons } from "../EventDetailSeriesNavButtons";
import { EventDetailTitleRow } from "../EventDetailTitleRow";
import { NewEventDetailBody$key } from "./__generated__/NewEventDetailBody.graphql";

type NewEventDetailBodyProps = {
  fragmentRef: NewEventDetailBody$key;
  /**
   * When true, only renders the event image (used in left column on desktop)
   */
  imageOnly?: boolean;
  /**
   * When true, hides the image and shows full info content (used in right column on desktop)
   */
  infoOnly?: boolean;
};

export const NewEventDetailBody = ({
  fragmentRef,
  imageOnly,
  infoOnly,
}: NewEventDetailBodyProps) => {
  const data = useFragment(NewEventDetailBodyFragment, fragmentRef);
  const publicId = data.eventSeries.image?.publicId;

  const isMultiSessionEvent =
    data.eventSessions?.edges?.length && data.eventSessions?.edges?.length > 1;

  // Image-only mode for desktop left column
  if (imageOnly) {
    return publicId ? (
      <VStack justifyContent="center" alignItems="center" mt={8}>
        <Box width="100%" borderRadius="2" overflow="hidden">
          <Image
            source={{
              uri: getThumbnail(publicId, 640, 360)?.toURL(),
            }}
            alt={t`Event cover image`}
            style={{ width: "100%", aspectRatio: 16 / 9 }}
          />
        </Box>
      </VStack>
    ) : null;
  }

  const DateTimeLocationComponent = isMultiSessionEvent ? (
    <EventDetailMultiSessionDateDisplay fragmentRef={data} />
  ) : (
    <EventDateTimeLocationBar
      fragmentRef={data.eventSessions?.edges?.[0]?.node ?? null}
    />
  );

  // Info-only mode for desktop right column (no image, includes title)
  if (infoOnly) {
    return (
      <VStack width="100%" pt={5} gap={4}>
        <EventDetailTitleRow fragmentRef={data} />
        {DateTimeLocationComponent}
        <EventDetailPairings fragmentRef={data} />
        <EventDetailSeriesNavButtons fragmentRef={data} />
        <EventDetailHostsRedesign
          fragmentRef={data.eventSessions?.edges?.[0]?.node ?? null}
        />
        <EventDetailCapacity fragmentRef={data} />
        {data.description ? (
          <EventDetailDescription description={data.description} />
        ) : null}
      </VStack>
    );
  }

  // Full mode for mobile (image + all info)
  return (
    <VStack justifyContent="center" alignItems="center" mt={8}>
      {publicId ? (
        <Box
          width="100%"
          maxWidth={{ base: "100%", lg: 536 }}
          borderRadius="2"
          overflow="hidden"
        >
          <Image
            source={{
              uri: getThumbnail(publicId, 640, 360)?.toURL(),
            }}
            alt={t`Event cover image`}
            style={{ width: "100%", aspectRatio: 16 / 9 }}
          />
        </Box>
      ) : null}
      <VStack width="100%" pt={{ base: 6, lg: 8 }} gap={4}>
        <EventDetailTitleRow fragmentRef={data} />
        {DateTimeLocationComponent}
        <EventDetailPairings fragmentRef={data} />
        <EventDetailSeriesNavButtons fragmentRef={data} />
        <EventDetailHostsRedesign
          fragmentRef={data.eventSessions?.edges?.[0]?.node ?? null}
        />
        <EventDetailCapacity fragmentRef={data} />
        {data.description ? (
          <EventDetailDescription description={data.description} />
        ) : null}
      </VStack>
    </VStack>
  );
};

export const NewEventDetailBodyFragment = graphql`
  fragment NewEventDetailBody on Event {
    ...EventDetailSeriesNavButtons_event
    ...EventDetailPairings_event
    ...EventDetailTitleRow
    ...EventDetailMultiSessionDateDisplay_event
    ...EventDetailCapacity_event
    description
    eventSessions(first: 1000) {
      edges {
        node {
          ...EventDateTimeLocationBar_eventSession
          ...EventDetailHosts
        }
      }
    }
    eventSeries {
      image {
        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
        publicId: renderingKey
      }
    }
  }
`;
